import isEmpty from 'lodash/isEmpty';

import { CompleteAddress, Country } from '@dreryk/address-common-api';
import {
   CurrentPractitionerQuery,
   CurrentPractitionerUpdateCommand,
   DoctorSpecialty as DoctorSpecialtyApi,
} from '@dreryk/egabinet-personnel-frontend-api';
import { FirstName, LastName } from '@dreryk/person-name-common-api';

import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { ActionBar, ButtonVariant, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';
import { useSessionState } from '@dreryk/egabinet-security-frontend';

import { personalDataTranslationKeys } from '../../../translations';

import {
   DoctorPersonalDataPart,
   PersonalDataBean,
   PersonalDataFormController,
   SpecialistPersonalDataPart,
} from './PersonalDataForm.types';
import { personalDataVisitor } from './PersonalDataForm.utils';
import { DoctorSpecialty } from './sections/DoctorSpecialtiesSection/DoctorSpecialtiesInteractiveTable';

const mapSpecialtiesWithoutEmptyMedicalSpecialtyToDoctorSpecialties = (
   doctorSpecialties: DoctorSpecialty[] | undefined
): DoctorSpecialtyApi[] => {
   if (!doctorSpecialties) {
      return [];
   }

   return doctorSpecialties.reduce<DoctorSpecialtyApi[]>((accumulator, specialty) => {
      if (!isEmpty(specialty.medicalSpecialty)) {
         accumulator.push({
            main: false,
            medicalSpecialty: specialty.medicalSpecialty!,
            specialtyLevel: specialty.specialtyLevel,
         });
      }
      return accumulator;
   }, []);
};

type PersonalDataUpdateActionProps = Pick<PersonalDataFormController, 'handleSubmit'> & {
   readonly onSuccess: VoidFunction;
   readonly updatingDoctorPersonalDataAvailable: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const PersonalDataUpdateAction = (props: PersonalDataUpdateActionProps): JSX.Element => {
   // TODO DREGAB-25028 Przeniesienie funkcjonalność integracji z Pharmindexem do panelu support
   const { setDisplayName } = useSessionState();
   const { executeAsync: updatePersonalData } = useCqrsCommand<
      void,
      PersonalDataBean<unknown | DoctorPersonalDataPart | SpecialistPersonalDataPart>
   >(
      (parameters) => {
         const command = new CurrentPractitionerUpdateCommand();
         command.firstName = FirstName.of(parameters.firstName!);
         command.lastName = LastName.of(parameters.lastName!);
         command.title = Choice.withUseDefaultIfNull(parameters.title);

         if (parameters.phoneNumber) {
            command.phoneNumber = Choice.of(parameters.phoneNumber);
         }

         personalDataVisitor({
            formData: parameters,
            updatingParamedicPersonalDataAvailable: props.updatingDoctorPersonalDataAvailable,
         })({
            withDoctor: ({
               medicalLicenceNumber,
               noSpecialty,
               doctorSpecialties,
               includeAddress,
               building,
               flat,
               street,
               town,
               postcode,
            }) => {
               command.medicalLicenceNumber = Choice.of(medicalLicenceNumber);
               command.specialty = Choice.of({
                  noSpecialty: noSpecialty,
                  doctorSpecialties: noSpecialty
                     ? []
                     : mapSpecialtiesWithoutEmptyMedicalSpecialtyToDoctorSpecialties(doctorSpecialties),
               });
               if (includeAddress) {
                  command.address = Choice.of(
                     CompleteAddress.builder()
                        .withPostcode(postcode!)
                        .withTown(town!)
                        .withBuilding(building!)
                        .withStreet(street)
                        .withFlat(flat)
                        .withCountry(Country.DEFAULT)
                        .build()
                  );
               }
            },
            withSpecialist: ({ pesel, paramedicalProfession }) => {
               command.pesel = Choice.of(pesel);

               if (paramedicalProfession) {
                  command.paramedicalProfessions = [paramedicalProfession];
               }
            },
         });

         return command;
      },
      {
         onSuccess: (__, formData) => {
            props.onSuccess();
            dispatchSnackbarPositiveEvent(personalDataTranslationKeys.personalDataUpdateSuccess);
            refetchCqrsQuery(CurrentPractitionerQuery);
            setDisplayName({
               firstName: formData.firstName!,
               lastName: formData.lastName!,
            });
         },
      }
   );

   return (
      <ActionBar.PrimaryButton
         testId={'updatePersonalData'}
         label={commonTranslationKeys.save}
         onClick={props.handleSubmit(updatePersonalData)}
         variant={ButtonVariant.PRIMARY}
         blockingOperation
      />
   );
};
